/**
 * Error codes the API emits.
 *
 * The frontend's copy of the backend catalogue: the message a person sees is
 * chosen by the frontend from the code, never from the text the server sends.
 * That way a message can be rewritten in the backend without breaking the
 * interface.
 */
export const ApiErrorCode = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EXTERNAL_VERIFICATION_FAILED: 'EXTERNAL_VERIFICATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_ALREADY_GRANTED: 'USER_ALREADY_GRANTED',
  LAST_ACTIVE_USER: 'LAST_ACTIVE_USER',
  GROUP_NOT_FOUND: 'GROUP_NOT_FOUND',
  GROUP_NAME_TAKEN: 'GROUP_NAME_TAKEN',
  /**
   * The session is valid and the permission is missing. Not a 401: refreshing
   * changes nothing, so the HTTP client must not treat it as an expired session.
   */
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  /** Not from the API: the request never went out, or there was no answer. */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /**
   * The upload was cancelled, by the person or by leaving the screen.
   *
   * Its own code because it is the one failure that needs no message: nobody has to
   * be told that what they just cancelled was cancelled.
   */
  UPLOAD_CANCELLED: 'UPLOAD_CANCELLED',

  // Virtual Profiler. The `VP_` prefix keeps the analysis domain's groups and
  // configurations from colliding with the access list's own.
  VP_UNAVAILABLE: 'VP_UNAVAILABLE',
  VP_RUN_NOT_FOUND: 'VP_RUN_NOT_FOUND',
  VP_RUN_NOT_CANCELLABLE: 'VP_RUN_NOT_CANCELLABLE',
  VP_RUN_PERSISTING: 'VP_RUN_PERSISTING',
  VP_RUN_NOT_QUEUED: 'VP_RUN_NOT_QUEUED',
  VP_RUN_NOT_PROMOTABLE: 'VP_RUN_NOT_PROMOTABLE',
  VP_RUN_PIN_CONFLICT: 'VP_RUN_PIN_CONFLICT',
  VP_RUN_NOT_REPLAYABLE: 'VP_RUN_NOT_REPLAYABLE',
  VP_FORCE_CANCEL_DISABLED: 'VP_FORCE_CANCEL_DISABLED',
  VP_GROUP_NOT_FOUND: 'VP_GROUP_NOT_FOUND',
  /** The group never ran anything under that execution label. */
  VP_GROUP_BATCH_NOT_FOUND: 'VP_GROUP_BATCH_NOT_FOUND',
  /** The group's parameters wait while one of its executions runs; its name never does. */
  VP_GROUP_EXECUTION_RUNNING: 'VP_GROUP_EXECUTION_RUNNING',
  /**
   * Pausing, resuming or deleting an execution whose state does not allow it: it is not paused, it
   * has nothing waiting, or something of it is still in flight.
   */
  VP_EXECUTION_STATE_CONFLICT: 'VP_EXECUTION_STATE_CONFLICT',
  /** The owner has no stored dataset version under that number: an old link. */
  VP_DATASET_VERSION_NOT_FOUND: 'VP_DATASET_VERSION_NOT_FOUND',
  /** Nothing to version: the dataset is exactly its latest stored version. */
  VP_DATASET_UNCHANGED: 'VP_DATASET_UNCHANGED',
  VP_GROUP_NAME_TAKEN: 'VP_GROUP_NAME_TAKEN',
  VP_GROUP_HAS_ACTIVE_RUNS: 'VP_GROUP_HAS_ACTIVE_RUNS',
  VP_GROUP_CONFIG_MISSING: 'VP_GROUP_CONFIG_MISSING',
  VP_GROUP_RUN_INVALID: 'VP_GROUP_RUN_INVALID',
  VP_CONFIG_NOT_FOUND: 'VP_CONFIG_NOT_FOUND',
  VP_CONFIG_NAME_TAKEN: 'VP_CONFIG_NAME_TAKEN',
  VP_CONFIG_IN_USE: 'VP_CONFIG_IN_USE',
  /** «Estándar» cannot be built from what the engine publishes: its catalogues are unusable. */
  VP_CONFIG_BASE_UNAVAILABLE: 'VP_CONFIG_BASE_UNAVAILABLE',
  /** Copying or activating a configuration that was never saved with engines. */
  VP_CONFIG_WITHOUT_ENGINES: 'VP_CONFIG_WITHOUT_ENGINES',
  /** Deleting the active configuration: another has to be activated first. */
  VP_CONFIG_ACTIVE: 'VP_CONFIG_ACTIVE',
  VP_VIDEO_NOT_FOUND: 'VP_VIDEO_NOT_FOUND',
  /**
   * The video cannot take another analysis now: uploading, being analysed, a run already in
   * flight, or Virtual Profiler's cap of runs per video. One code for the three, because upstream
   * answers all of them with the same bare 409.
   */
  VP_VIDEO_BUSY: 'VP_VIDEO_BUSY',
  VP_ENGINE_SELECTION_INVALID: 'VP_ENGINE_SELECTION_INVALID',
  VP_ANALYSIS_CONFIG_REQUIRED: 'VP_ANALYSIS_CONFIG_REQUIRED',
  /**
   * A launch went out and Virtual Profiler did not answer in time: it may have been queued. Not
   * `VP_UNAVAILABLE`, whose answer is «try again» — here that would launch it twice.
   */
  VP_LAUNCH_UNCONFIRMED: 'VP_LAUNCH_UNCONFIRMED',
  VP_DELETE_VARIANT_UNAVAILABLE: 'VP_DELETE_VARIANT_UNAVAILABLE',
  /** The video is there and has no gesture label with that id: somebody removed it meanwhile. */
  VP_GESTURE_LABEL_NOT_FOUND: 'VP_GESTURE_LABEL_NOT_FOUND',
  /** Virtual Profiler refused a gesture label: a gesture it cannot label, or times it rejects. */
  VP_GESTURE_LABEL_INVALID: 'VP_GESTURE_LABEL_INVALID',
  /** The video is there and has no incoherence range with that id: somebody removed it meanwhile. */
  VP_INCOHERENCE_RANGE_NOT_FOUND: 'VP_INCOHERENCE_RANGE_NOT_FOUND',
  /** Virtual Profiler refused an incoherence range: its end not after its start. */
  VP_INCOHERENCE_RANGE_INVALID: 'VP_INCOHERENCE_RANGE_INVALID',
  /** A control kind Virtual Profiler does not know: this build and upstream disagree on them. */
  VP_CONTROL_KIND_UNKNOWN: 'VP_CONTROL_KIND_UNKNOWN',
  /** The video's transcription is already on its way or done: there is nothing to ask again. */
  VP_TRANSCRIPTION_BUSY: 'VP_TRANSCRIPTION_BUSY',
  /** The person was renamed or assigned from a list that went stale. */
  VP_PERSON_NOT_FOUND: 'VP_PERSON_NOT_FOUND',
  /** Another person already has that name, whatever its case. */
  VP_PERSON_NAME_TAKEN: 'VP_PERSON_NAME_TAKEN',
  /** The CV is being analysed: it cannot be deleted until that finishes. */
  VP_CV_BUSY: 'VP_CV_BUSY',
  /** The CV has no extracted data yet: there is nothing to edit. */
  VP_CV_NOT_EDITABLE: 'VP_CV_NOT_EDITABLE',
  /** A CV or a video of the person is in progress: the person cannot be deleted yet. */
  VP_PERSON_BUSY: 'VP_PERSON_BUSY',
  /** The simulation expired (they last 24 h) or never existed. */
  VP_SIMULATION_NOT_FOUND: 'VP_SIMULATION_NOT_FOUND',
  /** The coherence configuration sent to simulate lacks what the engine needs. */
  VP_SIMULATION_CONFIG_INCOMPLETE: 'VP_SIMULATION_CONFIG_INCOMPLETE',
  /** The video has no stored analysis with ideas to simulate on. */
  VP_SIMULATION_NO_ANALYSIS: 'VP_SIMULATION_NO_ANALYSIS',
  /** The video already has a simulation waiting or running. */
  VP_SIMULATION_IN_PROGRESS: 'VP_SIMULATION_IN_PROGRESS',
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode | string,
    message: string,
    readonly status: number,
    readonly traceId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}
